import { Button } from "../UIElements/Button";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@finitless/shared/hooks";
import {
  apiClient,
  handleApiError,
  handleApiSuccess,
} from "@finitless/shared/lib";
import ImageUploader from "../UIElements/ImageUploader";
import { Icon } from "../UIElements/Icon";
import { NativeSelect } from "../UIElements/Select";
import { Switch } from "../UIElements/Switch";
import { useBrand } from "@finitless/shared/contexts";
import { useQueryClient } from "@tanstack/react-query";
import {
  getMissingRequiredFields,
  buildApiPayload,
  sanitizeApiPayload,
} from "./discountsConstants";
import PromotionRulesModal from "./PromotionRulesModal";
import ParticipatingStoresSelector from "./ParticipatingStoresSelector";
import PromotionTimeline from "./PromotionTimeline";

const defaultFormValues = {
  name: "",
  description: "",
  coupon: "",
  type: 1,
  target: 1,
  rate_type: 1,
  rate: "",
  stackable: false,
  auto: true,
  start: "",
  end: "",
  image: "",
  stores: [],
  // Advanced rules
  limit: null,
  limit_per_user: null,
  max_discount: null,
  minimum: null,
  user_order_count: null,
  user_order_count_condition: null,
  valid_from_after_user_last_order_minutes: null,
  valid_until_after_user_last_order_minutes: null,
  include_options: false,
  order_types_allowed: null,
  paymethods: null,
  delivery_zones: null,
  schedule: null,
  products: null,
  categories: null,
};

const CreatePromotionForm = ({
  onClose,
  onSubmit,
  initialData,
  openRulesOnMount = false,
  onConsumeOpenRulesOnMount,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const normalizeNullableArray = (value) =>
    Array.isArray(value) && value.length > 0 ? value : null;

  const [formState, setFormState] = useState({
    values: defaultFormValues,
  });

  const [actionState, setActionState] = useState({
    // isGenerating: false,
    isSubmitting: false,
  });

  const {
    values: {
      name,
      description,
      coupon,
      type: promotionType,
      target,
      rate_type,
      rate,
      stackable,
      auto,
      start,
      end,
      image,
      limit,
      limit_per_user,
      max_discount,
      minimum,
      user_order_count,
      user_order_count_condition,
      valid_from_after_user_last_order_minutes,
      valid_until_after_user_last_order_minutes,
      include_options,
      order_types_allowed,
      paymethods,
      delivery_zones,
      schedule,
      products,
      categories,
    },
  } = formState;

  const { /*isGenerating, */ isSubmitting } = actionState;

  const updateFormValues = (nextValuesOrUpdater) => {
    if (typeof nextValuesOrUpdater === "function") {
      setFormState((previous) => ({
        ...previous,
        values: nextValuesOrUpdater(previous.values),
      }));
      return;
    }

    setFormState((previous) => ({
      ...previous,
      values: {
        ...previous.values,
        ...nextValuesOrUpdater,
      },
    }));
  };

  const updateActionState = (changes) => {
    setActionState((previous) => ({
      ...previous,
      ...changes,
    }));
  };

  const [selectedStores, setSelectedStores] = useState([]);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const lastSavedStoresRef = useRef(null);
  const isAutosavingRef = useRef(false);

  const toast = useToast();
  const { brandData: businesses } = useBrand();

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const normalizeNullableNumber = (value) =>
      typeof value === "number" && !Number.isNaN(value) ? value : null;

    const normalizeNullableString = (value) =>
      typeof value === "string" && value.trim() ? value : null;

    const normalizedOrderTypesAllowed = normalizeNullableArray(
      initialData.raw?.order_types_allowed,
    );

    const normalizedPaymethods = normalizeNullableArray(
      Array.isArray(initialData.raw?.paymethods)
        ? initialData.raw.paymethods.map((item) => item?.id).filter(Boolean)
        : null,
    );

    const normalizedDeliveryZones = normalizeNullableArray(
      Array.isArray(initialData.raw?.delivery_zones)
        ? initialData.raw.delivery_zones.map((item) => item?.id).filter(Boolean)
        : null,
    );

    const normalizedProducts = normalizeNullableArray(
      initialData.raw?.products,
    );
    const normalizedCategories = normalizeNullableArray(
      initialData.raw?.categories,
    );

    const initialPromotionType =
      Number(initialData.raw?.type ?? initialData.type ?? 1) === 2 ? 2 : 1;

    updateFormValues((previousValues) => ({
      ...previousValues,
      name: initialData.name || "",
      description: initialData.description || "",
      coupon: initialPromotionType === 2 ? initialData.coupon || "" : "",
      type: initialPromotionType,
      rate_type: initialData.rate_type || 1,
      rate: initialData.rate ?? "",
      target: initialData.target || 1,
      stackable: Boolean(initialData.stackable),
      auto:
        typeof initialData.raw?.auto === "boolean"
          ? initialData.raw.auto
          : previousValues.auto,
      start: initialData.start || "",
      end: initialData.end || "",
      image: initialData.image || "",
      limit: normalizeNullableNumber(initialData.raw?.limit),
      limit_per_user: normalizeNullableNumber(initialData.raw?.limit_per_user),
      max_discount: normalizeNullableNumber(initialData.raw?.max_discount),
      minimum: normalizeNullableNumber(initialData.raw?.minimum),
      user_order_count: normalizeNullableNumber(
        initialData.raw?.user_order_count,
      ),
      user_order_count_condition: normalizeNullableString(
        initialData.raw?.user_order_count_condition,
      ),
      valid_from_after_user_last_order_minutes: normalizeNullableNumber(
        initialData.raw?.valid_from_after_user_last_order_minutes,
      ),
      valid_until_after_user_last_order_minutes: normalizeNullableNumber(
        initialData.raw?.valid_until_after_user_last_order_minutes,
      ),
      include_options: Boolean(initialData.raw?.include_options),
      order_types_allowed: normalizedOrderTypesAllowed,
      paymethods: normalizedPaymethods,
      delivery_zones: normalizedDeliveryZones,
      schedule: initialData.raw?.schedule || null,
      products: normalizedProducts,
      categories: normalizedCategories,
    }));

    if (
      initialData.raw?.businesses &&
      Array.isArray(initialData.raw.businesses)
    ) {
      setSelectedStores(initialData.raw.businesses.map((store) => store.id));
      lastSavedStoresRef.current = initialData.raw.businesses.map(
        (store) => store.id,
      );
    }
  }, [initialData]);

  useEffect(() => {
    if (!openRulesOnMount) {
      return;
    }

    setIsRulesModalOpen(true);
    if (onConsumeOpenRulesOnMount) {
      onConsumeOpenRulesOnMount();
    }
  }, [openRulesOnMount, onConsumeOpenRulesOnMount]);

  const promotionId = initialData?.raw?.id || initialData?.id || null;
  const isEditingExistingPromotion = Boolean(promotionId);

  const serializeCategoriesRule = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "string") {
      return value;
    }

    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    const cleaned = value
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const id = Number(item.id);
        if (!Number.isFinite(id)) {
          return null;
        }

        const include = typeof item.include === "boolean" ? item.include : true;
        return { id, include, is_condition: true };
      })
      .filter(Boolean);

    if (cleaned.length === 0) {
      return null;
    }

    try {
      return JSON.stringify(cleaned);
    } catch {
      return null;
    }
  };

  const serializeProductsRule = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "string") {
      return value;
    }

    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    const cleaned = value
      .map((item) => {
        if (!item || typeof item !== "object") {
          return null;
        }

        const id = Number(item.id);
        if (!Number.isFinite(id)) {
          return null;
        }

        const include = typeof item.include === "boolean" ? item.include : true;
        return { id, include, is_condition: true };
      })
      .filter(Boolean);

    if (cleaned.length === 0) {
      return null;
    }

    try {
      return JSON.stringify(cleaned);
    } catch {
      return null;
    }
  };

  const handleQuickUpdatePromotion = async (patch) => {
    if (!isEditingExistingPromotion) {
      return;
    }

    if (!patch || typeof patch !== "object") {
      return;
    }

    if (isAutosavingRef.current) {
      return;
    }

    isAutosavingRef.current = true;

    try {
      const payload = { ...patch };

      if (Object.prototype.hasOwnProperty.call(payload, "categories")) {
        payload.categories = serializeCategoriesRule(payload.categories);
      }

      if (Object.prototype.hasOwnProperty.call(payload, "products")) {
        payload.products = serializeProductsRule(payload.products);
      }

      const cleanPayload = sanitizeApiPayload(payload);
      if (Object.keys(cleanPayload).length === 0) {
        return;
      }

      await apiClient.post(`/offers/${promotionId}`, cleanPayload);

      queryClient.invalidateQueries({ queryKey: ["promotions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["promotion"], exact: false });

      handleApiSuccess("Offer updated successfully", toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to update promotion",
        defaultMessage:
          "There was an error updating the promotion. Please try again.",
      });
    } finally {
      isAutosavingRef.current = false;
    }
  };

  const handleCommitSelectedStores = async (nextStores) => {
    const normalized = Array.isArray(nextStores) ? nextStores : [];
    const previousSaved = Array.isArray(lastSavedStoresRef.current)
      ? lastSavedStoresRef.current
      : [];

    const hasChanged =
      JSON.stringify([...previousSaved].sort()) !==
      JSON.stringify([...normalized].sort());
    if (!hasChanged) {
      return;
    }

    lastSavedStoresRef.current = normalized;

    if (!isEditingExistingPromotion) {
      return;
    }

    await handleQuickUpdatePromotion({ businesses: normalized });
  };

  const handleImageChange = (file, base64) => {
    updateFormValues({
      image: base64,
    });
  };

  const handleChangeStartDate = (value) => {
    updateFormValues((previous) => {
      const next = {
        ...previous,
        start: value,
      };

      if (!value) {
        return next;
      }

      const newStart = new Date(value);

      if (Number.isNaN(newStart.getTime())) {
        return next;
      }

      const currentEnd = next.end ? new Date(next.end) : null;

      if (!currentEnd || currentEnd <= newStart) {
        const nextDay = new Date(newStart);
        nextDay.setDate(newStart.getDate() + 1);
        const nextDayIso = nextDay.toISOString().split("T")[0];
        next.end = nextDayIso;
      }

      return next;
    });
  };

  const handleChangeEndDate = (value) => {
    if (!value) {
      updateFormValues({ end: value });
      return;
    }

    if (!start) {
      updateFormValues({ end: value });
      return;
    }

    const startDateObj = new Date(start);
    const endDateObj = new Date(value);

    if (
      Number.isNaN(startDateObj.getTime()) ||
      Number.isNaN(endDateObj.getTime())
    ) {
      updateFormValues({ end: value });
      return;
    }

    if (endDateObj <= startDateObj) {
      toast.error("End date must be after start date", {
        description:
          "Please select an end date that is later than the start date.",
      });
      return;
    }

    updateFormValues({ end: value });
  };

  const handleChangeRate = (event) => {
    const { value } = event.target;

    if (value === "") {
      updateFormValues({ rate: "" });
      return;
    }

    // Disallow negative numbers (including unicode minus).
    if (String(value).trim().startsWith("-") || String(value).includes("−")) {
      return;
    }

    const numericRegex = /^\d+(\.\d*)?$/;

    if (!numericRegex.test(value)) {
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return;
    }

    if (numericValue < 0) {
      return;
    }

    if (rate_type === 1 && numericValue > 100) {
      return;
    }

    updateFormValues({ rate: value });
  };

  const handleChangeRateType = (nextType) => {
    if (nextType === 1 && rate_type === 2) {
      const numericValue = Number(rate);

      if (!Number.isNaN(numericValue) && numericValue > 100) {
        updateFormValues({ rate: "100" });
      }
    }

    updateFormValues({ rate_type: nextType });
  };

  const handleChangePromotionType = (nextType) => {
    const normalizedType = Number(nextType) === 2 ? 2 : 1;

    updateFormValues((previous) => {
      if (previous.type === normalizedType) {
        return previous;
      }

      return {
        ...previous,
        type: normalizedType,
      };
    });
  };

  // const handleMagicGenerate = () => {
  //   if (!name) {
  //     return;
  //   }

  //   updateActionState({ isGenerating: true });

  //   const suggestedCode = name.replace(/\s+/g, '').toUpperCase().slice(0, 10) || 'PROMO';
  //   const discountLabel =
  //     rate_type === 1 ? 'a percentage discount' : 'a fixed amount discount';

  //   setTimeout(() => {
  //     updateFormValues({
  //       description: `Enjoy ${discountLabel.toLowerCase()} with our "${name}" promotion. Encourage customers to complete their order and try more of your menu.`,
  //       coupon: suggestedCode,
  //     });
  //     updateActionState({ isGenerating: false });
  //   }, 400);
  // };

  const handleOpenRulesModal = async () => {
    const trimmedName = name.trim();

    const missingFields = getMissingRequiredFields({
      name: trimmedName,
      start,
      end,
      selectedStores,
    });

    if (missingFields.length > 0) {
      toast.error("Missing required fields", {
        description: `Please complete: ${missingFields.join(", ")}`,
      });
      return;
    }

    if (isEditingExistingPromotion) {
      setIsRulesModalOpen(true);
      return;
    }

    if (isSubmitting) {
      return;
    }

    updateActionState({ isSubmitting: true });

    try {
      const todayIso = new Date().toISOString().split("T")[0];
      const nextWeekIso = new Date(Date.now() + 86400000 * 7)
        .toISOString()
        .split("T")[0];

      const startDateValue = start || todayIso;
      const endDateValue = end || nextWeekIso;
      const imageUrl = image;

      // Create payload: required fields + API defaults (or user overrides if changed).
      const payloadToSend = sanitizeApiPayload({
        enabled: true,
        auto: Boolean(auto),
        public: true,
        condition_type: 1,
        type: promotionType,
        target: Number(target) || 1,
        rate_type: Number(rate_type) || 1,
        stackable: Boolean(stackable),
        rate: Number(rate) || 0,
        name: trimmedName,
        description,
        coupon: promotionType === 2 ? coupon : undefined,
        image: imageUrl,
        start: `${startDateValue} 00:00:00`,
        end: `${endDateValue} 23:59:59`,
        businesses: selectedStores,
      });
      const response = await apiClient.post("/offers", payloadToSend);

      const rawPromotion = response?.result || null;
      if (!rawPromotion?.id) {
        throw new Error("Promotion created but missing id");
      }

      // Trigger refetch in background.
      queryClient.invalidateQueries({ queryKey: ["promotions"], exact: false });

      // Build minimal UI promotion shape used by DiscountsPage
      const uiPromotion = {
        id: rawPromotion.id,
        name: rawPromotion.name,
        description: rawPromotion.description,
        coupon: rawPromotion.coupon,
        image: rawPromotion.image,
        type: rawPromotion.type,
        rate: rawPromotion.rate,
        rate_type: rawPromotion.rate_type,
        target: rawPromotion.target,
        stackable: Boolean(rawPromotion.stackable),
        start: String(rawPromotion.start || "").split(" ")[0] || "",
        end: String(rawPromotion.end || "").split(" ")[0] || "",
        enabled: rawPromotion.enabled,
        raw: rawPromotion,
      };

      handleApiSuccess("Promotion created successfully", toast);

      navigate(`/discounts/${rawPromotion.id}`, {
        state: { promotion: uiPromotion, openRulesModal: true },
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to create promotion",
        defaultMessage:
          "There was an error creating the promotion. Please try again.",
      });
    } finally {
      updateActionState({ isSubmitting: false });
    }
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();

    const missingFields = getMissingRequiredFields({
      name: trimmedName,
      start,
      end,
      selectedStores,
    });

    if (missingFields.length > 0) {
      toast.error("Missing required fields", {
        description: `Please complete: ${missingFields.join(", ")}`,
      });
      return;
    }

    if (isSubmitting) {
      return;
    }

    updateActionState({ isSubmitting: true });

    const todayIso = new Date().toISOString().split("T")[0];
    const nextWeekIso = new Date(Date.now() + 86400000 * 7)
      .toISOString()
      .split("T")[0];

    const startDateValue = start || todayIso;
    const endDateValue = end || nextWeekIso;

    const imageUrl = image;

    const baseApiPayload = buildApiPayload({
      initialData,
      name: trimmedName,
      description,
      coupon,
      promotionType,
      imageUrl,
      startDateValue,
      endDateValue,
      rate_type,
      rate,
      target,
      stackable,
      auto,
      selectedStores,
      selectedProducts: products,
      selectedCategories: categories,
      limit,
      limit_per_user,
      max_discount,
      minimum,
      user_order_count,
      user_order_count_condition,
      valid_from_after_user_last_order_minutes,
      valid_until_after_user_last_order_minutes,
      include_options,
      order_types_allowed,
      paymethods,
      delivery_zones,
      schedule,
    });

    const apiPayload = baseApiPayload;

    const promotionId = initialData?.raw?.id || initialData?.id;
    const isUpdate = Boolean(promotionId);

    let payloadToSend;

    if (isUpdate && initialData) {
      const originalSelectedStores =
        initialData.raw?.businesses?.map((store) => store.id) || [];

      const originalBasePayload = buildApiPayload({
        initialData,
        name: initialData.name || "",
        description: initialData.description || "",
        coupon: initialData.coupon || "",
        promotionType: Number(initialData.raw?.type ?? initialData.type ?? 1),
        imageUrl: initialData.image || "",
        startDateValue: initialData.start || startDateValue,
        endDateValue: initialData.end || endDateValue,
        rate_type: initialData.rate_type,
        rate: initialData.rate,
        target: initialData.target,
        stackable: Boolean(initialData.stackable),
        auto:
          typeof initialData.raw?.auto === "boolean"
            ? initialData.raw.auto
            : undefined,
        selectedStores: originalSelectedStores,
        selectedProducts: normalizeNullableArray(initialData.raw?.products),
        selectedCategories: normalizeNullableArray(initialData.raw?.categories),
        limit: initialData.raw?.limit ?? null,
        limit_per_user: initialData.raw?.limit_per_user ?? null,
        max_discount: initialData.raw?.max_discount ?? null,
        minimum: initialData.raw?.minimum ?? null,
        user_order_count: initialData.raw?.user_order_count ?? null,
        user_order_count_condition:
          initialData.raw?.user_order_count_condition ?? null,
        valid_from_after_user_last_order_minutes:
          initialData.raw?.valid_from_after_user_last_order_minutes ?? null,
        valid_until_after_user_last_order_minutes:
          initialData.raw?.valid_until_after_user_last_order_minutes ?? null,
        include_options: Boolean(initialData.raw?.include_options),
        order_types_allowed: normalizeNullableArray(
          initialData.raw?.order_types_allowed,
        ),
        paymethods: normalizeNullableArray(
          Array.isArray(initialData.raw?.paymethods)
            ? initialData.raw.paymethods.map((item) => item?.id).filter(Boolean)
            : null,
        ),
        delivery_zones: normalizeNullableArray(
          Array.isArray(initialData.raw?.delivery_zones)
            ? initialData.raw.delivery_zones
                .map((item) => item?.id)
                .filter(Boolean)
            : null,
        ),
        schedule: initialData.raw?.schedule || null,
      });

      const originalApiPayload = originalBasePayload;

      const cleanCurrent = sanitizeApiPayload(apiPayload);
      const cleanOriginal = sanitizeApiPayload(originalApiPayload);

      const diffPayload = Object.fromEntries(
        Object.entries(cleanCurrent).filter(([key, value]) => {
          const originalValue = cleanOriginal[key];
          return JSON.stringify(value) !== JSON.stringify(originalValue);
        }),
      );

      if (Object.keys(diffPayload).length === 0) {
        updateActionState({ isSubmitting: false });
        if (onClose) {
          onClose();
        }
        return;
      }

      payloadToSend = diffPayload;
    } else {
      // Creation: required fields + API defaults (or user overrides if changed)
      payloadToSend = sanitizeApiPayload({
        enabled: true,
        auto: Boolean(auto),
        public: true,
        condition_type: 1,
        type: promotionType,
        target: Number(target) || 1,
        rate_type: Number(rate_type) || 1,
        stackable: Boolean(stackable),
        rate: Number(rate) || 0,
        name: trimmedName,
        description,
        coupon: promotionType === 2 ? coupon : undefined,
        image: imageUrl,
        start: `${startDateValue} 00:00:00`,
        end: `${endDateValue} 23:59:59`,
        businesses: selectedStores,
      });
    }

    try {
      const response = await apiClient.post(
        promotionId ? `/offers/${promotionId}` : "/offers",
        payloadToSend,
      );

      handleApiSuccess(
        promotionId
          ? "Promotion updated successfully"
          : "Promotion created successfully",
        toast,
      );

      // Refresh promotion lists after creating/updating
      queryClient.invalidateQueries({ queryKey: ["promotions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["promotion"], exact: false });

      if (onSubmit) {
        // Pass the promotion returned by the API (result) to update local state
        onSubmit(response?.result || null);
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      handleApiError(error, toast, {
        title: promotionId
          ? "Failed to update promotion"
          : "Failed to create promotion",
        defaultMessage:
          "There was an error saving the promotion. Please try again.",
      });
    } finally {
      updateActionState({ isSubmitting: false });
    }
  };

  return (
    <div className="ordering-discounts-form animate-fade-in relative flex h-full flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-foreground text-2xl font-semibold">
            {initialData ? "Edit Promotion" : "Create Promotion"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {initialData
              ? "Update discount details"
              : "Configure your new discount campaign"}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:bg-background hover:text-foreground rounded-lg px-4 py-2 text-[length:var(--font-size-product-ui)] font-medium transition-colors"
          >
            Cancel
          </button>
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="px-6 py-2"
          >
            {isSubmitting
              ? initialData
                ? "Updating..."
                : "Publishing..."
              : initialData
                ? "Update Promotion"
                : "Publish Campaign"}
          </Button>
        </div>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto pr-2 pb-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-6 lg:col-span-1 xl:col-span-2">
            <div className="border-border bg-background rounded-xl border p-6">
              <h3 className="text-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
                <span className="bg-primary h-6 w-1 rounded-full" />
                General Information
              </h3>

              <div className="space-y-5">
                <div>
                  <label className="text-muted-foreground mb-1.5 block text-[length:var(--font-size-product-ui)] font-medium">
                    Promotion Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) =>
                      updateFormValues({
                        name: event.target.value,
                      })
                    }
                    placeholder="e.g. Summer Burger Fiesta"
                    className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 transition-all outline-none focus:border-transparent focus:ring-2"
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-muted-foreground block text-[length:var(--font-size-product-ui)] font-medium">
                      Description
                    </label>
                    {/** TODO: Add AI generation */}
                    {/* <button
                      type="button"
                      onClick={handleMagicGenerate}
                      disabled={isGenerating || !name}
                      className="text-xs flex items-center gap-1 text-primary hover:text-primary disabled:opacity-50 transition-colors"
                    >
                      <Sparkles size={12} />
                      {isGenerating ? 'Generating...' : 'Generate with AI'}
                    </button> */}
                  </div>
                  <textarea
                    value={description}
                    onChange={(event) =>
                      updateFormValues({
                        description: event.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Write a little description or let AI help you..."
                    className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full resize-none rounded-lg border px-4 py-2.5 transition-all outline-none focus:border-transparent focus:ring-2"
                  />
                </div>

                <div>
                  <label className="text-muted-foreground mb-1.5 block text-[length:var(--font-size-product-ui)] font-medium">
                    Cover Image
                  </label>
                  <ImageUploader
                    className="group border-border text-muted-foreground hover:border-primary hover:bg-background/40 relative flex h-48 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition-all"
                    onImageChange={handleImageChange}
                  >
                    {image ? (
                      <>
                        <img
                          src={image}
                          className="absolute inset-0 h-full w-full object-cover opacity-50 transition-opacity group-hover:opacity-30"
                          alt="preview"
                        />
                        <div className="z-10 flex flex-col items-center">
                          <Icon
                            name="Upload"
                            size={20}
                            className="mb-2 text-white"
                          />
                          <span className="text-foreground text-xs">
                            Change Image
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bg-background mb-3 flex h-12 w-12 items-center justify-center rounded-full transition-transform group-hover:scale-110">
                          <Icon
                            name="Upload"
                            size={20}
                            className="text-primary"
                          />
                        </div>
                        <span className="text-muted-foreground group-hover:text-foreground text-sm font-medium">
                          Click to upload or drag image
                        </span>
                        <span className="text-muted-foreground mt-1 text-xs">
                          PNG, JPG up to 2MB
                        </span>
                      </>
                    )}
                  </ImageUploader>
                </div>

                <ParticipatingStoresSelector
                  businesses={businesses}
                  selectedStores={selectedStores}
                  onChangeSelectedStores={setSelectedStores}
                  onCommitSelectedStores={handleCommitSelectedStores}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-1 xl:col-span-1">
            <PromotionTimeline
              start={start}
              end={end}
              onChangeStart={handleChangeStartDate}
              onChangeEnd={handleChangeEndDate}
            />

            <div className="border-border bg-background rounded-xl border p-6">
              <h3 className="text-muted-foreground mb-4 text-sm font-semibold tracking-wider uppercase">
                Value &amp; Logic
              </h3>

              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="promotion-target"
                    className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                  >
                    Applies To
                  </label>
                  <div className="relative">
                    <NativeSelect
                      id="promotion-target"
                      value={target}
                      onChange={(event) =>
                        updateFormValues({
                          target: Number(event.target.value),
                        })
                      }
                      className="border-border bg-background focus:ring-ring text-foreground w-full cursor-pointer appearance-none rounded-lg border py-2.5 pr-10 pl-4 transition-all outline-none focus:border-transparent focus:ring-2"
                    >
                      <option value={1}>Order Subtotal</option>
                      <option value={2}>Delivery Fee</option>
                      <option value={3}>Service Fee</option>
                    </NativeSelect>
                    <Icon
                      name="ChevronDown"
                      size={16}
                      className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium">
                    Discount Method
                  </label>
                  <div className="bg-background grid grid-cols-2 gap-2 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => handleChangeRateType(1)}
                      aria-pressed={rate_type === 1}
                      className={`flex items-center justify-center gap-2 rounded-md py-2 text-[length:var(--font-size-product-ui)] font-medium transition-all ${
                        rate_type === 1
                          ? "bg-card text-white shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon name="Percent" size={14} /> Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChangeRateType(2)}
                      aria-pressed={rate_type === 2}
                      className={`flex items-center justify-center gap-2 rounded-md py-2 text-[length:var(--font-size-product-ui)] font-medium transition-all ${
                        rate_type === 2
                          ? "bg-card text-white shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon name="DollarSign" size={14} /> Fixed
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-muted-foreground mb-1.5 block text-[length:var(--font-size-product-ui)] font-medium">
                    Value
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={rate}
                    onChange={handleChangeRate}
                    placeholder="0"
                    onWheel={(event) => {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }}
                    onKeyDown={(event) => {
                      // Block non-numeric keys except control/navigation keys and dot
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Home",
                        "End",
                      ];

                      if (allowedKeys.includes(event.key)) {
                        return;
                      }

                      const isNumberKey = /^[0-9]$/.test(event.key);
                      const isDot = event.key === ".";

                      if (!isNumberKey && !isDot) {
                        event.preventDefault();
                      }

                      if (isDot && event.currentTarget.value.includes(".")) {
                        event.preventDefault();
                      }
                    }}
                    className="border-border bg-background focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 text-left font-mono transition-all outline-none focus:border-transparent focus:ring-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="promotion-type"
                    className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                  >
                    How it&apos;s going to be applied?
                  </label>
                  <div className="relative">
                    <NativeSelect
                      id="promotion-type"
                      value={promotionType}
                      onChange={(event) =>
                        handleChangePromotionType(event.target.value)
                      }
                      disabled={isSubmitting}
                      className="border-border bg-background focus:ring-ring text-foreground w-full cursor-pointer appearance-none rounded-lg border py-2.5 pr-10 pl-4 transition-all outline-none focus:border-transparent focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Select how the promotion is applied"
                    >
                      <option value={1}>Discount</option>
                      <option value={2}>Coupon</option>
                    </NativeSelect>
                    <Icon
                      name="ChevronDown"
                      size={16}
                      className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                </div>
                {promotionType === 2 ? (
                  <div>
                    <label className="text-muted-foreground mb-1.5 block text-[length:var(--font-size-product-ui)] font-medium">
                      Coupon code
                    </label>
                    <input
                      type="text"
                      value={coupon}
                      onChange={(event) =>
                        updateFormValues({
                          coupon: event.target.value.toUpperCase(),
                        })
                      }
                      placeholder="SUMMER24"
                      className="border-border bg-background text-primary placeholder:text-muted-foreground focus:ring-ring w-full rounded-lg border px-4 py-2.5 font-semibold tracking-widest uppercase transition-all outline-none focus:border-transparent focus:ring-2"
                    />
                  </div>
                ) : null}

                <div className="border-border space-y-4 border-t pt-2">
                  <button
                    type="button"
                    onClick={handleOpenRulesModal}
                    className="group border-primary/20 bg-accent/50 hover:border-primary/40 hover:bg-accent flex w-full items-center justify-between rounded-xl border p-4 shadow-sm transition-all"
                  >
                    <div className="flex flex-col items-start text-left">
                      <span className="text-foreground mt-1 text-sm font-medium">
                        Configure Rules
                      </span>
                      <span className="text-primary/80 mt-0.5 text-xs">
                        {limit ||
                        limit_per_user ||
                        max_discount ||
                        minimum ||
                        (Array.isArray(order_types_allowed) &&
                          order_types_allowed.length > 0) ||
                        (Array.isArray(paymethods) && paymethods.length > 0) ||
                        (Array.isArray(delivery_zones) &&
                          delivery_zones.length > 0)
                          ? "Custom conditions configured"
                          : "No conditions set"}
                      </span>
                    </div>
                    <div className="bg-accent/80 text-foreground flex h-9 w-9 items-center justify-center rounded-full">
                      <Icon name="ChevronDown" className="h-4 w-4 -rotate-90" />
                    </div>
                  </button>

                  <div className="border-border bg-background flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <span className="text-foreground block text-sm font-medium">
                        Combine Offers
                      </span>
                      <span className="text-muted-foreground text-xs">
                        Can be used with other discounts?
                      </span>
                    </div>
                    <Switch
                      checked={Boolean(stackable)}
                      onCheckedChange={(enabled) =>
                        updateFormValues((previous) => ({
                          ...previous,
                          stackable: Boolean(enabled),
                        }))
                      }
                      aria-label="Toggle stackable discount"
                    />
                  </div>

                  <div className="border-border bg-background flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <span className="text-foreground block text-sm font-medium">
                        Auto Apply
                      </span>
                      <span className="text-muted-foreground text-xs">
                        Apply this discount automatically?
                      </span>
                    </div>
                    <Switch
                      checked={Boolean(auto)}
                      onCheckedChange={(enabled) =>
                        updateFormValues((previous) => ({
                          ...previous,
                          auto: Boolean(enabled),
                        }))
                      }
                      aria-label="Toggle auto apply discount"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PromotionRulesModal
        isOpen={isRulesModalOpen}
        onClose={() => setIsRulesModalOpen(false)}
        selectedBusinessIds={selectedStores}
        businesses={businesses}
        onQuickSave={handleQuickUpdatePromotion}
        rules={{
          limit,
          limit_per_user,
          max_discount,
          minimum,
          user_order_count,
          user_order_count_condition,
          valid_from_after_user_last_order_minutes,
          valid_until_after_user_last_order_minutes,
          include_options,
          order_types_allowed,
          paymethods,
          delivery_zones,
          schedule,
          products,
          categories,
        }}
        onApplyRules={(nextRules) => {
          updateFormValues(nextRules);
        }}
      />
    </div>
  );
};

export default CreatePromotionForm;
